/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useBetween } from "use-between";
import Papa from "papaparse"; // Add this import for CSV parsing

import useShopify from "../../useShopify";
import Brand from "./brand";
import Gifts from "./gifts";
import Details from "./details";
import Address from "./address";
import Complete from "./complete";
import Review from "./review";
import EmailInput from "./brand-email";
import { countryList } from "./address/country";

const REGENT_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"];
// Placeholder SKU option for the World Cruise 2027 hat / belt bag pick; the gifts
// step matches those products by exclusion (anything that isn't a polo, vest or jacket).
const WORLD_CRUISE_2027_ACCESSORY_OPTION = "world-cruise-hat or world-cruise-belt-bag";

// Custom hook for managing step history
const useStepHistory = (initialStep) => {
  const [step, setStep] = useState(initialStep);

  useEffect(() => {
    // Push initial state
    window.history.pushState({ step: initialStep }, '');

    // Handle browser back/forward navigation
    const handlePopState = (event) => {
      if (event.state && typeof event.state.step === 'number') {
        setStep(event.state.step);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [initialStep]);

  const setStepWithHistory = (newStep) => {
    window.history.pushState({ step: newStep }, '');
    setStep(newStep);
  };

  return [step, setStepWithHistory];
};

const Client = () => {
  const { fetchProducts, getCampaign } = useBetween(useShopify);
  const [step, setStep] = useStepHistory(0);
  const { campaign, company } = useParams();

  // Helper function to format company name
  const formatCompanyName = (companyName) => {
    if (companyName === "JAMF") {
      return "Jamf";
    }
    return companyName;
  };

  const [newSkuOptions, setNewSkuOptions] = useState([]);
  const [newSizeOptions, setNewSizeOptions] = useState([]);
  const [newGenders, setNewGenders] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isOutlier, setIsOutlier] = useState(false);
  const [hasShownWelcomeModal, setHasShownWelcomeModal] = useState(false);
  const isRegent = company === "REGENT-SEVEN SEAS";

  // useEffect(() => {
  //   if (isRegent) {
  //     // fetch this localhost:5001/api/v1/booking/2688407
  //     fetch("/2025-WC-Pax-Manifest.csv")
  //       .then((response) => response.text())
  //       .then((csvText) => {
  //         const results = Papa.parse(csvText, {
  //           delimiter: ";", // Set the delimiter to semicolon
  //           header: false, // We don't have headers, so set this to false
  //         });
  //         console.log("results", results.data);
  //         setCsvData(results.data);
  //       });
  //   }
  // }, [isRegent]);

  const isEncore = company === "Encore-Global";
  const isLiveNation = company === "LIVE-NATION SPECIAL EVENTS";
  const isWiz = company === "Wiz";
  const isGusto = company === "Gusto-New Hire Gifts 2025";
  const isImpiricus = company === "Impiricus";
  const isKlingAI = company === "Kling-AI" //68643ac2cee86b5fec9574f1

  const isNewCyera = campaign === "CYERA-SECRET SANTA";
  const isPhilo = campaign === "Philo-Holiday 2025";

  const isPinterestKatie2025 = company === "Pinterest-(Katie - June 2025)";

  const isCapgeminiCampaign = campaign === "Capgemini-Winter Gifts 2026";
  const isBPMHoliday2026 = campaign === "BPM-Holiday Gifts 2026";

  // World Cruise 2027 skips the Details step; the Address step already shows the selection summary.
  const isRegentWorldCruise2027 = isRegent && (campaign || '').toLowerCase().replace(/[-\s]/g, '').includes('worldcruise2027');

  // console.log('isWiz', company);
  const [form, setForm] = useState({
    email: "",
    size: "",
    firstName: "",
    lastName: "",
    companyName: isLiveNation || isRegent || isWiz || isImpiricus || isKlingAI ? "" : isPinterestKatie2025 ? "Pinterest" : company,
    phoneNumber: "",
    streetAddress: "",
    suite: "",
    postalCode: "",
    city: "",
    country: "",
    state: "",
    venueName: "",
    marketingCode: "",
    country: "United States (US)",
    affiliatedBrands: "",
  });

  const [selectionState, setSelectionState] = useState({
    sizeOptions: [],
    genderOptions: [],
    skuOptions: [],
    selections: [],
    currentGenderIndex: 0,
    currentOptionIndex: 0,
    selectedSizes: {},
    isSelectionOpen: true,
  });

  // scenario1: male female
  // scenario2: male male
  // scenario3: female female
  // scenario4: male
  // scenario5: female
  // male sku 1 or sku 2, sku 3 or sku 4
  // female sku 5 or sku 6, sku 7 or sku 8
  // sizes XS, S, M, L, XL, 2XL, 3XL

  const updateSelectionState = (newState) => {
    setSelectionState((prevState) => ({ ...prevState, ...newState }));
  };

  const updateFormBasedOnBooking = (booking, nextValue) => {
    fetch(`${process.env.REACT_APP_API_URL}/booking/${booking}`)
      .then((response) => response.json())
      .then((res) => {
        const data = res.data;
        if (data) {
          let newSkuOptions = [];
          let newSizeOptions = [];
          let newGenders = [];
          let isOutlier = false;

          const processGenderOptions = (scenario, campaignName) => {
            console.log('Processing scenario:', { scenario, campaignName });
            
            // Normalize campaign name for comparison
            const normalizedCampaignName = (campaignName || '').toLowerCase().replace(/[-\s]/g, '');

            const isArcticCampaign = normalizedCampaignName.includes('grandarctic');
            const isWorldCruise = normalizedCampaignName.includes('worldcruise2026') || normalizedCampaignName.includes('worldcruise2027');
            // World Cruise 2027 adds a third pick per guest: hat or belt bag.
            const isWorldCruise2027 = normalizedCampaignName.includes('worldcruise2027');
            const isCulturalCampaign = normalizedCampaignName.includes('grandculturalquest');
            const isContinentalCampaign = normalizedCampaignName.includes('grandcontinental');
            const isAsiaCampaign = normalizedCampaignName.includes('grandasia') || normalizedCampaignName.includes('grandasianexploration') || normalizedCampaignName.includes('indianodyssey');
            // Grand Asia 2026 reuses the Grand Arctic products (Indian Odyssey / Grand Asian Exploration keep their own).
            const isGrandAsia2026 = normalizedCampaignName.includes('grandasia2026');

            console.log('Campaign detection in processGenderOptions:', {
              campaignName,
              normalizedCampaignName,
              isArcticCampaign,
              isCulturalCampaign,
              isContinentalCampaign
            });

            const arcticSkus = {
              male: "eddie-bauer-mens-fleece-vest-grand-arctic or mens-sport-tek-full-zip-wind-jacket-grand-arctic",
              female: "port-authority-womens-core-soft-shell-vest-grand-arctic or port-authority-zephyr-full-zip-jacket-grand-arctic"
            };

            const culturalSkus = {
              male: "eddie-bauer-mens-fleece-vest-grand-quest or mens-sport-tek-full-zip-wind-jacket-grand-quest",
              female: "port-authority-womens-core-soft-shell-vest-grand-quest or port-authority-zephyr-full-zip-jacket-grand-quest"
            };

            const continentalSkus = {
              male: "eddie-bauer-mens-fleece-vest-grand-continental or mens-sport-tek-full-zip-wind-jacket-grand-continental",
              female: "port-authority-womens-core-soft-shell-vest-grand-continental or port-authority-zephyr-full-zip-jacket-grand-continental"
            };

            const asiaSkus = {
              male: "grand-asia-vest-mens or grand-asia-insulated-jacket-mens",
              female: "grand-asia-vest-women or grand-asia-insulated-jacket-womens"
            };

            const worldCruiseSkus = {
              male: {
                polo: "world-cruise-travis-mathew-polo",
                outer: "world-cruise-eddie-bauer-vest-mens or world-cruise-insulated-jacket-mens"
              },
              female: {
                polo: "world-cruise-travis-mathew-polo-womens",
                outer: "world-cruise-eddie-bauer-vest-womens or world-cruise-insulated-jacket-womens"
              }
            };

            const targetSkus = (isArcticCampaign || isGrandAsia2026)
              ? arcticSkus
              : isCulturalCampaign
                ? culturalSkus
                : isContinentalCampaign
                  ? continentalSkus
                  : isAsiaCampaign
                    ? asiaSkus
                    : continentalSkus;

            if (isWorldCruise2027) {
              const guestOptions = (gender) => [
                worldCruiseSkus[gender].polo,
                worldCruiseSkus[gender].outer,
                WORLD_CRUISE_2027_ACCESSORY_OPTION
              ];
              const guestSizes = () => [REGENT_SIZES, REGENT_SIZES, REGENT_SIZES];
              switch (scenario) {
                case "scenario1": // Male/Female
                  newSkuOptions = [guestOptions("male"), guestOptions("female")];
                  newSizeOptions = [...guestSizes(), ...guestSizes()];
                  newGenders = ["Guest 1", "Guest 2"];
                  break;
                case "scenario2": // Male/Male
                  newSkuOptions = [guestOptions("male"), guestOptions("male")];
                  newSizeOptions = [...guestSizes(), ...guestSizes()];
                  newGenders = ["Guest 1", "Guest 2"];
                  break;
                case "scenario3": // Female/Female
                  newSkuOptions = [guestOptions("female"), guestOptions("female")];
                  newSizeOptions = [...guestSizes(), ...guestSizes()];
                  newGenders = ["Guest 1", "Guest 2"];
                  break;
                case "scenario4": // Single Male
                  newSkuOptions = [guestOptions("male")];
                  newSizeOptions = guestSizes();
                  newGenders = ["Guest 1"];
                  break;
                case "scenario5": // Single Female
                  newSkuOptions = [guestOptions("female")];
                  newSizeOptions = guestSizes();
                  newGenders = ["Guest 1"];
                  break;
                case "OUTLIER":
                  isOutlier = true;
                  break;
                default:
                  console.warn('Unknown scenario:', scenario);
                  break;
              }
            } else if (isWorldCruise) {
              switch (scenario) {
                case "scenario1": // Male/Female
                  newSkuOptions = [
                    [worldCruiseSkus.male.polo, worldCruiseSkus.male.outer],
                    [worldCruiseSkus.female.polo, worldCruiseSkus.female.outer]
                  ];
                  newSizeOptions = [
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]
                  ];
                  newGenders = ["Guest 1", "Guest 2"];
                  break;
                case "scenario2": // Male/Male
                  newSkuOptions = [
                    [worldCruiseSkus.male.polo, worldCruiseSkus.male.outer],
                    [worldCruiseSkus.male.polo, worldCruiseSkus.male.outer]
                  ];
                  newSizeOptions = [
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]
                  ];
                  newGenders = ["Guest 1", "Guest 2"];
                  break;
                case "scenario3": // Female/Female
                  newSkuOptions = [
                    [worldCruiseSkus.female.polo, worldCruiseSkus.female.outer],
                    [worldCruiseSkus.female.polo, worldCruiseSkus.female.outer]
                  ];
                  newSizeOptions = [
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]
                  ];
                  newGenders = ["Guest 1", "Guest 2"];
                  break;
                case "scenario4": // Single Male
                  newSkuOptions = [
                    [worldCruiseSkus.male.polo, worldCruiseSkus.male.outer]
                  ];
                  newSizeOptions = [
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]
                  ];
                  newGenders = ["Guest 1"];
                  break;
                case "scenario5": // Single Female
                  newSkuOptions = [
                    [worldCruiseSkus.female.polo, worldCruiseSkus.female.outer]
                  ];
                  newSizeOptions = [
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]
                  ];
                  newGenders = ["Guest 1"];
                  break;
                case "OUTLIER":
                  isOutlier = true;
                  break;
                default:
                  console.warn('Unknown scenario:', scenario);
                  break;
              }
            } else {
              switch (scenario) {
                case "scenario1": // Male/Female
                  newSkuOptions = [
                    [targetSkus.male],
                    [targetSkus.female]
                  ];
                  newSizeOptions = [
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]
                  ];
                  newGenders = ["Guest 1", "Guest 2"];
                  break;
                case "scenario2": // Male/Male
                  newSkuOptions = [
                    [targetSkus.male],
                    [targetSkus.male]
                  ];
                  newSizeOptions = [
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]
                  ];
                  newGenders = ["Guest 1", "Guest 2"];
                  break;
                case "scenario3": // Female/Female
                  newSkuOptions = [
                    [targetSkus.female],
                    [targetSkus.female]
                  ];
                  newSizeOptions = [
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"],
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]
                  ];
                  newGenders = ["Guest 1", "Guest 2"];
                  break;
                case "scenario4": // Single Male
                  newSkuOptions = [
                    [targetSkus.male]
                  ];
                  newSizeOptions = [
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]
                  ];
                  newGenders = ["Guest 1"];
                  break;
                case "scenario5": // Single Female
                  newSkuOptions = [
                    [targetSkus.female]
                  ];
                  newSizeOptions = [
                    ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]
                  ];
                  newGenders = ["Guest 1"];
                  break;
                case "OUTLIER":
                  isOutlier = true;
                  break;
                default:
                  console.warn('Unknown scenario:', scenario);
                  break;
              }
            }

            console.log('Processed options:', {
              scenario,
              newSkuOptions,
              newSizeOptions,
              newGenders
            });
          };

          const scenario = data.SCENARIO;
          processGenderOptions(scenario, campaign);

          // Update the state
          console.log('Updating state with:', {
            newSkuOptions,
            newSizeOptions,
            newGenders,
            isOutlier
          });

          setNewSkuOptions(newSkuOptions);
          setNewSizeOptions(newSizeOptions);
          setNewGenders(newGenders);
          setIsOutlier(isOutlier);

          setForm((prevForm) => ({
            ...prevForm,
            email: data.EMAIL,
            firstName: data.FNAME,
            lastName: data.LNAME,
            streetAddress: data.ADDR1,
            suite: data.ADDR2,
            city: data.CITY,
            state: data.STATE,
            postalCode: data.ZIP,
            country: data.COUNTRY,
            phoneNumber: data.PHONE,
            bookingNumber: booking,
          }));

          updateSelectionState({
            skuOptions: newSkuOptions,
            sizeOptions: newSizeOptions,
            genderOptions: newGenders,
            selections: newGenders.map(() => []),
            currentGenderIndex: 0,
            currentOptionIndex: 0,
            selectedSizes: {},
            isSelectionOpen: true,
          });

          if (nextValue && !isOutlier) {
            setStep(nextValue);
          }
        } else {
          setErrorMessage(
            "The booking number you entered was not found in our system."
          );
          console.error("Booking not found in CSV data");
        }
      });
  };

  useEffect(() => {
    console.log('Fetching campaign for:', { company, campaign });
    getCampaign(formatCompanyName(company), campaign).then((res) => {
      // console.log('Campaign response:', res);
      const products = res.campaign.products;
      // console.log('Products from campaign:', products);
      fetchProducts(products);
    });
  }, []);

  // Effect to initialize selectionState based on skuOptions
  useEffect(() => {
    // console.log("CAMPAIGN", company);
    if (
      isRegent &&
      selectionState.skuOptions.length > 0 &&
      selectionState.selections.length === 0
    ) {
      updateSelectionState({
        selections: selectionState.skuOptions.map((gender) =>
          gender.map(() => null)
        ),
      });
    }
  }, [isRegent, selectionState.skuOptions]);

  return (
    <>
      {step === 0 && (
        <Brand
          form={form}
          setForm={setForm}
          next={() => {
            if (isEncore) {
              setStep(4);
            } else if (isRegent) {
              setStep(1);
            } else if (isGusto || isKlingAI || isNewCyera || isCapgeminiCampaign || isBPMHoliday2026) {
              setStep(4);
            } else {
              setStep(2);
            }
          }}
        />
      )}
      {step === 1 && isRegent && (
        <EmailInput
          isOutlier={isOutlier}
          setErrorMessage={setErrorMessage}
          errorMessage={errorMessage}
          next={() => setStep(2)}
          updateFormBasedOnBooking={updateFormBasedOnBooking}
        />
      )}
      {step === 2 && (
        <Gifts
          prev={() => {
            isRegent ? setStep(1) : setStep(0);
          }}
          next={() => {
            isPhilo || isRegentWorldCruise2027 ? setStep(4) : setStep(3);
          }}
          gotoCheckout={() => {
            setStep(4);
          }}
          selectionState={selectionState}
          updateSelectionState={updateSelectionState}
          hasShownWelcomeModal={hasShownWelcomeModal}
          setHasShownWelcomeModal={setHasShownWelcomeModal}
          form={form}
          setForm={setForm}
        />
      )}
      {step === 3 && (
        <Details
          prev={() => setStep(2)}
          next={() => setStep(4)}
          form={form}
          setForm={setForm}
          selectionState={selectionState}
          updateSelectionState={updateSelectionState}
        />
      )}
      {step === 4 && (
        <Address
          prev={() => {
            if (isGusto || isKlingAI || isNewCyera || isCapgeminiCampaign || isBPMHoliday2026) {
              setStep(0);
            } else {
              isEncore ? setStep(0) : setStep(2);
            }
          }}
          next={() => setStep(5)}
          form={form}
          setForm={setForm}
          selectionState={selectionState}
        />
      )}
      {step === 5 &&
        (isLiveNation ? (
          <Review form={form} next={() => setStep(6)} prev={() => setStep(4)} />
        ) : (
          <Complete
            next={() => {
              setStep(0);
            }}
          />
        ))}
      {step === 6 && (
        <Complete
          next={() => {
            setStep(0);
          }}
        />
      )}
    </>
  );
};

export default Client;
